import { Context } from "../Engine";
import { mat4 } from "gl-matrix";
import { ITexture } from "./ITexture";

export class DataTexture implements ITexture {
    public glTexture: WebGLTexture;
    public translucent: boolean;
    public dimension: TextureDimension;
    private offsets: { [key: string]: number };

    constructor(protected ctx: Context) {
        this.offsets = {};
        this.translucent = false;
    }

    public getOffset(key: string): number {
        if (key in this.offsets) {
            return this.offsets[key];
        }

        throw new Error(`DataTexture offset not found for key "${key}"`);
    }

    public setMat4Data(matrixData: Mat4Set[]) {
        let data: number[] = [];
        let offset = 0;

        for (let kv of matrixData) {
            this.offsets[kv.key] = offset;

            for (let matrix of kv.matrices) {
                data.push(...matrix.values());
                offset++;
            }
        }


        this.createTexture(data);
    }

    private getTextureDimension(matrixCount: number): TextureDimension {
        // a mat4x4 matrix takes up 4 vec4s in the texture
        const vecDimension = 4;

        // find an optimal square size for the texture which can store all matrices
        let squareDimension = Math.ceil(Math.sqrt(matrixCount * vecDimension));

        // ensure that the width is a multiple of 4 so texture matrix reads are always from a single row
        let width = squareDimension + (vecDimension - (squareDimension % vecDimension)) % vecDimension;

        // calculate the height required, rounding up so everything will fit
        let height = width > 0 ? Math.ceil(matrixCount * vecDimension / width) : 0

        return {
            width: width,
            height: height,
            length: width * height * vecDimension
        }
    }

    private createTexture(data: number[]) {
        this.dimension = this.getTextureDimension(data.length / 16);

        // Add padding if required
        let buffer = new Float32Array(this.dimension.length);
        buffer.set(data);

        let gl = this.ctx.gl;
        let texture = gl.createTexture();
        this.glTexture = texture;

        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.dimension.width, this.dimension.height, 0, gl.RGBA, gl.FLOAT, buffer);

        gl.bindTexture(gl.TEXTURE_2D, null);

    }

}

export type Mat4Set = {
    key: string,
    matrices: mat4[]
};


type TextureDimension = {
    width: number,
    height: number,
    length: number
};